import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ArrowRight, RefreshCw } from "lucide-react";
import { api, asItems, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Offices() {
  const [items, setItems] = useState([]);
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const params = city ? `?city=${encodeURIComponent(city)}` : "";
    let live = true;
    setLoading(true);
    setError("");
    api.get(`/offices${params}`)
      .then(({ data }) => { if (live) setItems(asItems(data)); })
      .catch((requestError) => {
        if (live) {
          setItems([]);
          setError(formatApiError(requestError));
        }
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [city, reloadKey]);

  return (
    <div className="premium-page-surface bg-transparent min-h-screen" data-testid="offices-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">Offices</p>
            {/* Brand gradient accent on the lead word */}
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-primary"><span className="brand-gradient-text">Registered</span> real-estate firms</h1>
          </div>
          <Input placeholder="Filter by city…" value={city} onChange={(e) => setCity(e.target.value)} className="md:w-80" data-testid="offices-city-filter"/>
        </div>

        {error && <div className="mb-8 rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-center" role="alert">
          <p className="font-bold text-primary">Offices could not be loaded</p>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
          <Button type="button" variant="outline" className="mt-4" onClick={() => setReloadKey((key) => key + 1)}><RefreshCw className="mr-2 h-4 w-4" />Try again</Button>
        </div>}
        {loading ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" aria-label="Loading offices">
          {[0, 1, 2].map((item) => <div key={item} className="h-64 rounded-2xl border border-border shimmer" />)}
        </div> : !error && <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {/*
            WHY these fallbacks exist:
              The backend used to return raw offices columns (id/name/city…)
              which caused every card to link to /offices/undefined and show
              blanks. The backend is now fixed to return office_id /
              office_name / state / description / agent_count / property_count,
              but we keep the `||` fallbacks so this page also works with:
                - the old backend, and
                - any client that receives a mocked response in tests.
              This keeps the page resilient without duplicating render logic.
          */}
          {items.map((o, i) => {
            const officeId = o.office_id || o.id;
            const officeName = o.office_name || o.name;
            const state = o.state || (o.raw && o.raw.state);
            const description = o.description || (o.raw && o.raw.description);
            const agentCount = o.agent_count ?? 0;
            const propertyCount = o.property_count ?? 0;
            return (
              <Link key={officeId} to={`/offices/${officeId}`} style={{ animationDelay: `${Math.min(i, 12) * 60}ms`, animationFillMode: "both" }} className="premium-card premium-card-interactive bg-background border border-border rounded-2xl p-6 hover:-translate-y-1 transition group animate-fade-up" data-testid={`office-item-${officeId}`}>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 ring-1 ring-primary/10 text-primary grid place-items-center mb-4 shadow-sm group-hover:scale-110 group-hover:-rotate-3 group-hover:ring-primary/25 transition-all duration-300 animate-gradient-pan"><Building2 className="w-6 h-6 group-hover:animate-icon-bob"/></div>
                <p className="font-display font-bold text-lg text-primary">{officeName}</p>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{o.city}{state ? `, ${state}` : ""}</p>
                {description && <p className="text-sm text-muted-foreground mt-4 line-clamp-2">{description}</p>}
                <div className="border-t border-border mt-5 pt-4 flex items-center justify-between text-xs font-bold text-primary">
                  <span>{agentCount} agents</span>
                  <span>{propertyCount} listings</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition"/>
                </div>
              </Link>
            );
          })}
        </div>}
        {!loading && !error && items.length === 0 && <p className="text-muted-foreground text-center py-20">No offices found.</p>}
      </div>
    </div>
  );
}
